// The newsletter as email code, beside the PDF.
//
// Session 53 (Wave 15a). Leena, 2026-10-13: "Mailchimp lets you import emails
// from code so we'd be able to download it as code. That would be the way to
// go." The droplet keeps two email flavours of every newsletter next to the
// PDF: one for Mailchimp (its merge tags and editable regions, the footer line
// Mailchimp requires) and one plain, for any email tool that takes your own
// HTML. Both carry hosted images, so they paste in and send as they are.
//
// Same bucket, same folder as the PDF: the storage read policy that lets a
// client sign a URL for their package covers these files too. A download is
// a signed URL with a filename. The preview cannot be the signed URL itself:
// storage serves HTML as text/plain on purpose (the browser would show the
// source), so the file is fetched and shown inside a sandboxed iframe in a
// new tab, the way the onboarding preview does it (session 46 review: never
// a page of this origin). The tab is opened before the first await so a
// popup blocker does not eat it.
//
// 2026-10-14, Leena's Mailchimp test: "Code your own" offers Paste in code,
// Import ZIP and Import from URL, and the Import ZIP picker refuses a bare
// .html. So For Mailchimp downloads a zip holding the one HTML file (styles
// inline, images hosted, so nothing else needs to go in it), made here in the
// browser (src/lib/zip.js); the plain flavour stays an .html.
import { useState } from 'react';
import { supabase } from '../supabase.js';
import { zipStored } from '../lib/zip.js';

async function signedUrl(path, download) {
  const { data, error } = await supabase.storage.from('post-packages')
    .createSignedUrl(path, 300, download ? { download } : undefined);
  if (error) throw error;
  return data.signedUrl;
}

function wrapped(html, title) {
  const attr = html.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  return `<!doctype html><title>${title}</title>`
    + '<style>html,body{margin:0;height:100%;background:#F5F8FA}iframe{border:0;width:100%;height:100%}</style>'
    + `<iframe sandbox="" srcdoc="${attr}"></iframe>`;
}

export default function NewsletterCode({ row }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState('');
  if (!row?.newsletter_code_ready) return null;
  const base = `newsletter-${row.slug}`;

  async function download(key, path, filename) {
    try {
      setBusy(key);
      window.location.href = await signedUrl(path, filename);
    } catch (e) {
      alert(e.message);
    } finally {
      setBusy('');
    }
  }

  async function downloadZip(path, innerName, zipName) {
    try {
      setBusy('mc');
      const resp = await fetch(await signedUrl(path));
      if (!resp.ok) throw new Error(`Could not load the newsletter (HTTP ${resp.status})`);
      // the bytes as stored, not decoded and re-encoded (review: keeps a BOM or odd byte intact)
      const bytes = zipStored([{ name: innerName, data: new Uint8Array(await resp.arrayBuffer()) }]);
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url; a.download = zipName; a.rel = 'noopener';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      alert(e.message);
    } finally {
      setBusy('');
    }
  }

  async function preview() {
    const w = window.open('', '_blank');
    if (w) w.opener = null;
    try {
      setBusy('preview');
      const resp = await fetch(await signedUrl(row.newsletter_html_path));
      if (!resp.ok) throw new Error(`Could not load the newsletter (HTTP ${resp.status})`);
      const page = wrapped(await resp.text(), 'Newsletter preview');
      if (w) {
        w.document.open(); w.document.write(page); w.document.close();
      } else {
        const url = URL.createObjectURL(new Blob([page], { type: 'text/html' }));
        window.location.href = url;
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      }
    } catch (e) {
      if (w) w.close();
      alert(e.message);
    } finally {
      setBusy('');
    }
  }

  return (
    <span className="nl-code">
      <button type="button" className="btn" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        {open ? 'Hide email code' : 'Email code'}
      </button>
      {open && (
        <span className="nl-code-menu" role="group" aria-label="The newsletter as email code">
          <button type="button" className="btn btn-small" disabled={!!busy}
                  onClick={() => downloadZip(row.newsletter_mailchimp_path, `${base}-mailchimp.html`, `${base}-mailchimp.zip`)}>
            For Mailchimp (zip)
          </button>
          <button type="button" className="btn btn-small" disabled={!!busy}
                  onClick={() => download('plain', row.newsletter_html_path, `${base}.html`)}>
            For any email tool
          </button>
          <button type="button" className="btn btn-small" disabled={!!busy} onClick={preview}>
            Preview in browser
          </button>
        </span>
      )}
    </span>
  );
}
